import React, { useMemo, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faBoxOpen, faCheck, faClose, faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons"
import { useTranslation } from "react-i18next"
import ModalPortal from "../../components/Modal/ModalPortal"
import Animate, { AnimationTypes } from "../../components/Animate/Animate"
import { TitlePageResponsiveIconSmall } from "../../components/Titles/Titles"
import defaultStyle from "../../styles/defaultPageStyle.module.css"
import THEME from "../../configuration/Theme"
import { SIZE } from "../../configuration/Sizes"
import { ButtonIcon } from "../../components/Button/Button"
import InputField from "../../components/InputField/InputField"
import { useConfirmation } from "../../context/useConfirmationContext"
import type { SelectionEntity } from "../../Objects/Selection/SelectionEntity"
import type { SelectionOptionEntity } from "../../Objects/Selection/SelectionOptionEntity"
import type { SelectionOptionStockEntity } from "../../Objects/Selection/SelectionOptionStockEntity"

interface SelectionOptionStockPopupProps {
  selections: SelectionEntity[]
  canEdite: boolean
  pendingCount: number
  getOptionStockConfig: (option: SelectionOptionEntity) => SelectionOptionStockEntity
  onOptionStockControlChange: (option: SelectionOptionEntity, hasStockControl: boolean) => void
  onOptionStockQuantityChange: (option: SelectionOptionEntity, stockQuantity: number) => void
  onSaveAll: () => Promise<boolean>
  onClose: () => void
}

const rowGridClass = "grid min-w-[640px] grid-cols-[minmax(160px,1fr)_140px_160px_130px] items-center gap-2"

const quantityFieldId = (optionId: number) => `selection-option-stock-quantity-${optionId}`

const toggleFieldId = (optionId: number) => `selection-option-stock-toggle-${optionId}`

export const SelectionOptionStockPopup: React.FC<SelectionOptionStockPopupProps> = ({
  selections,
  canEdite,
  pendingCount,
  getOptionStockConfig,
  onOptionStockControlChange,
  onOptionStockQuantityChange,
  onSaveAll,
  onClose,
}) => {
  const { t } = useTranslation("genericFoodEntity")

  const [search, setSearch] = useState("")

  const { setNewConfirmation } = useConfirmation()

  const groups = useMemo(() => {
    const normalizedSearch = search.trim().toLowerCase()

    return selections
      .map((selection) => ({
        selection,
        options: selection.options.filter((option) =>
          normalizedSearch ? option.name.toLowerCase().includes(normalizedSearch) : true,
        ),
      }))
      .filter((group) => group.options.length > 0)
  }, [selections, search])

  const orderedOptionIds = useMemo(
    () => groups.flatMap((group) => group.options.map((option) => option.id)),
    [groups],
  )

  const requestClose = () => {
    if (pendingCount > 0) {
      setNewConfirmation(
        t("label_option_stock_close_confirmation_title"),
        t("label_option_stock_close_confirmation_description"),
        onClose,
      )
    } else {
      onClose()
    }
  }

  const focusNextRow = (currentOptionId: number) => {
    const nextOptionId = orderedOptionIds[orderedOptionIds.indexOf(currentOptionId) + 1]

    if (nextOptionId === undefined) return

    const nextElement =
      document.getElementById(quantityFieldId(nextOptionId)) ?? document.getElementById(toggleFieldId(nextOptionId))

    nextElement?.focus()
  }

  const handleQuantityKeyDown = (option: SelectionOptionEntity) => (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      event.preventDefault()
      focusNextRow(option.id)
    }
  }

  return (
    <ModalPortal>
      <div className={defaultStyle.overlay}>
        <Animate type={AnimationTypes.slideUpIn} duration="0.3s">
          <div className={defaultStyle.alert2}>
            <TitlePageResponsiveIconSmall
              title={t("title_option_stock_popup")}
              icon={faBoxOpen}
              iconColor={THEME.colors.iconBGColor}
              children={
                <ButtonIcon
                  sizeNumber={SIZE.buttonCloseIconSize}
                  bgColor={THEME.colors.buttonBGCloseColor}
                  iconColor={THEME.colors.buttonFGCloseColor}
                  icon={faClose}
                  onClick={requestClose}
                  size="small"
                  descriptionKey="interactionDescriptions:pages.products.selectionOptionStockPopup.selectionOptionStockPopup.button.requestClose"
                />
              }
            />

            <div className="flex flex-col gap-3 p-2.5">
              <Animate type={AnimationTypes.fadeInTraslate} duration="0.35s" delay="0.08s">
                <InputField
                  type="text"
                  placeholder={t("label_option_stock_search_placeholder")}
                  icon={faMagnifyingGlass}
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  descriptionKey="interactionDescriptions:pages.products.selectionOptionStockPopup.selectionOptionStockPopup.input.search"
                />
              </Animate>

              <Animate type={AnimationTypes.fadeInTraslate} duration="1.2s">
                <section className="overflow-x-auto rounded-lg border border-slate-200">
                  <div className={`${rowGridClass} bg-slate-100 px-2.5 py-2 text-xs font-extrabold text-slate-700`}>
                    <span>{t("label_option_stock_column_option")}</span>
                    <span>{t("label_option_stock_column_control")}</span>
                    <span>{t("label_option_stock_column_quantity")}</span>
                    <span>{t("label_option_stock_column_status")}</span>
                  </div>

                  {groups.length === 0 && (
                    <div className="px-2.5 py-4 text-center text-sm text-slate-500">
                      {t("label_option_stock_empty_search")}
                    </div>
                  )}

                  {groups.map((group) => (
                    <React.Fragment key={group.selection.id}>
                      <div className="border-t border-slate-200 bg-orange-50 px-2.5 py-1.5 text-xs font-extrabold text-orange-700">
                        {group.selection.name}
                      </div>

                      {group.options.map((option) => {
                        const stock = getOptionStockConfig(option)

                        const status = !stock.hasStockControl
                          ? {
                              label: t("label_option_stock_status_unlimited"),
                              className: "bg-slate-100 text-slate-600",
                            }
                          : stock.isOutOfStock
                            ? {
                                label: t("label_option_stock_status_out_of_stock"),
                                className: "bg-rose-100 text-rose-700",
                              }
                            : {
                                label: t("label_option_stock_status_in_stock"),
                                className: "bg-emerald-100 text-emerald-700",
                              }

                        const rowIndex = orderedOptionIds.indexOf(option.id)

                        return (
                          <Animate
                            key={option.id}
                            type={AnimationTypes.fadeInTraslate}
                            duration="0.3s"
                            delay={`${Math.min(rowIndex, 10) * 0.03}s`}
                          >
                            <div className={`${rowGridClass} border-t border-slate-200 px-2.5 py-2`}>
                              <span className="truncate text-sm text-slate-800">{option.name}</span>

                              <input
                                id={toggleFieldId(option.id)}
                                className="h-4 w-4 accent-orange-600"
                                type="checkbox"
                                checked={stock.hasStockControl}
                                disabled={canEdite}
                                aria-label={t("label_option_has_stock_control")}
                                onChange={(event) => onOptionStockControlChange(option, event.target.checked)}
                                data-interaction-description-key="interactionDescriptions:pages.products.selectionOptionStockPopup.selectionOptionStockPopup.toggle.hasStockControl"
                              />

                              <div onKeyDown={handleQuantityKeyDown(option)}>
                                <InputField
                                  id={quantityFieldId(option.id)}
                                  type="number"
                                  placeholder={t("label_option_stock_quantity_place_holder")}
                                  onInvalidMessage={t("label_stock_quantity_onInvalidMessage")}
                                  value={String(stock.stockQuantity)}
                                  onChange={(event) =>
                                    onOptionStockQuantityChange(option, Number(event.target.value) || 0)
                                  }
                                  disable={canEdite || !stock.hasStockControl}
                                  min={0}
                                  descriptionKey="interactionDescriptions:pages.products.selectionOptionStockPopup.selectionOptionStockPopup.input.quantity"
                                />
                              </div>

                              <span
                                className={`inline-flex w-fit items-center rounded-full px-2 py-1 text-[11px] font-semibold ${status.className}`}
                              >
                                {status.label}
                              </span>
                            </div>
                          </Animate>
                        )
                      })}
                    </React.Fragment>
                  ))}
                </section>
              </Animate>

              <span className="text-xs font-semibold text-slate-500">{t("label_option_stock_shortcuts_hint")}</span>

              <div className={defaultStyle.divisionBar}></div>

              <Animate type={AnimationTypes.fadeInTraslate} duration="0.35s" delay="0.12s">
                <section className="flex flex-wrap items-center justify-between gap-2">
                  <span className="text-sm font-bold text-slate-700">
                    {pendingCount > 0
                      ? t("label_option_stock_pending_count", { count: pendingCount })
                      : t("label_option_stock_no_pending")}
                  </span>

                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg bg-green-600 px-3 text-sm font-bold text-white transition hover:bg-green-700 disabled:cursor-not-allowed disabled:opacity-55"
                      onClick={() => void onSaveAll()}
                      disabled={pendingCount === 0 || canEdite}
                      data-interaction-description-key="interactionDescriptions:pages.products.selectionOptionStockPopup.selectionOptionStockPopup.button.saveAll"
                    >
                      <FontAwesomeIcon icon={faCheck} />
                      {t("label_option_stock_save_button")}
                    </button>

                    <button
                      type="button"
                      className="inline-flex min-h-[42px] items-center justify-center gap-2 rounded-lg bg-red-600 px-3 text-sm font-bold text-white transition hover:bg-red-700"
                      onClick={requestClose}
                      data-interaction-description-key="interactionDescriptions:pages.products.selectionOptionStockPopup.selectionOptionStockPopup.button.requestClose2"
                    >
                      <FontAwesomeIcon icon={faClose} />
                      {t("buttons:button_cancel")}
                    </button>
                  </div>
                </section>
              </Animate>
            </div>
          </div>
        </Animate>
      </div>
    </ModalPortal>
  )
}
